import { AssistantGlyph } from '@/components/brand/AssistantGlyph'
import { useAgentSidebar } from '@/components/layout/AgentSidebarContext'
import { AiButton } from '@/components/ui/ai-button'
import { cn } from '@/utils/cn'

interface AskAgentActionProps {
  /** Custom prompt to display */
  prompt?: string
  /** Custom button label */
  label?: string
  /** Additional className for the container */
  className?: string
  /** Variant: 'default' shows full card, 'inline' shows just button */
  variant?: 'default' | 'inline' | 'compact'
}

/**
 * A reusable component to prompt users to ask the AI agent.
 * Use this in empty states, no-results screens, and other places
 * where the agent can help.
 */
export function AskAgentAction({
  prompt = 'Need help? Ask Xendrel Assistant',
  label = 'Ask Xendrel',
  className,
  variant = 'default',
}: AskAgentActionProps) {
  const agent = useAgentSidebar()

  if (variant === 'inline') {
    return (
      <AiButton
        assistant
        emphasis="subtle"
        className={className}
        onClick={() => agent.setOpen(true)}
      >
        {label}
      </AiButton>
    )
  }

  if (variant === 'compact') {
    return (
      <AiButton assistant emphasis="link" className={className} onClick={() => agent.setOpen(true)}>
        {label}
      </AiButton>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border border-dashed border-primary/30 bg-primary/5 p-6 text-center',
        className
      )}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
        <AssistantGlyph className="h-6 w-6" />
      </div>
      <p className="text-sm text-muted-foreground">{prompt}</p>
      <AiButton assistant emphasis="subtle" onClick={() => agent.setOpen(true)}>
        {label}
      </AiButton>
    </div>
  )
}
